import type { Offer, ShopLocation } from "@larder/shared";
import { httpsCallable } from "firebase/functions";
import { functions } from "./extract";

/**
 * Client side of shop prices.
 *
 * Every function here returns an empty answer rather than throwing. The screen
 * that uses them is finished without prices — five shop buttons and what you
 * last paid (ADR 0023) — so there is no error state to render and nothing a
 * caller could do with one. See docs/plans/active/0006-shopping-prices.md.
 */

/**
 * Mirrors the function's `PriceAnswer`. `none` is "nobody could be asked" —
 * the monthly SerpApi allowance is spent, or the call failed — as opposed to
 * `cache`/`live` with zero offers, which is a real "no shop lists this".
 */
export type PriceSource = "cache" | "live" | "stale" | "none";

interface PriceAnswer {
  offers: Offer[];
  at: number;
  source: PriceSource;
}

const NOTHING: PriceAnswer = { offers: [], at: 0, source: "none" };

const askPrices = httpsCallable<{ query: string; where: ShopLocation }, PriceAnswer>(
  functions,
  "shopPrices",
  { timeout: 30_000 },
);

const askWhere = httpsCallable<
  { lat: number; lng: number },
  { where: ShopLocation | null }
>(functions, "resolveShopLocation", { timeout: 30_000 });

/**
 * A memo on this device, on top of the server's week-long global cache.
 *
 * The quota is already safe without it: the server caches by product AND city
 * for seven days, so opening the same line every day for a week is one lookup
 * however many people do it (ADR 0024). What this saves is the round trip —
 * visible on a phone even when the answer comes back from a cache — and it
 * survives a reload, which a plain in-memory map does not. Someone who opens
 * the shopping list, checks a price, closes the app and comes back an hour
 * later should see the number immediately.
 *
 * Deliberately NOT stored on the item or the household. The authoritative copy
 * is the shared server cache; a per-household duplicate would be a second
 * answer to the same question, free to drift from the first.
 */
const MEMO_KEY = "larder.prices.v1";
/** Half a day. Long enough to cover a day's revisits, short enough to move. */
const MEMO_TTL_MS = 12 * 60 * 60 * 1000;
/** Bounded, because localStorage is a few megabytes shared with everything. */
const MEMO_MAX = 40;

type Memo = Record<string, PriceAnswer & { saved: number }>;

/**
 * Read fresh each time rather than held in a module variable.
 *
 * Two tabs of an installed PWA are two copies of this module and one
 * localStorage, and the cheap way to let them share is to not cache the cache.
 */
function readMemo(): Memo {
  try {
    return JSON.parse(localStorage.getItem(MEMO_KEY) ?? "{}") as Memo;
  } catch {
    // Private mode, a full disk, or something else's JSON under our key.
    return {};
  }
}

function writeMemo(memo: Memo): void {
  try {
    // Oldest out first, so a long shopping list cannot push the cap into
    // evicting the thing that was just looked at.
    const entries = Object.entries(memo).sort((a, b) => b[1].saved - a[1].saved);
    localStorage.setItem(
      MEMO_KEY,
      JSON.stringify(Object.fromEntries(entries.slice(0, MEMO_MAX))),
    );
  } catch {
    /* A price memo is not worth an error. */
  }
}

export async function fetchPrices(
  query: string,
  where: ShopLocation,
): Promise<PriceAnswer> {
  const key = `${query.toLowerCase()}|${where.canonicalName}`;
  const memo = readMemo();
  const held = memo[key];
  if (held && Date.now() - held.saved < MEMO_TTL_MS) return held;

  try {
    const res = await askPrices({ query, where });
    const answer = res.data ?? NOTHING;
    // Only a real answer is kept. Remembering "we could not ask" would turn
    // one bad moment into a dead feature for half a day.
    if (answer.offers.length > 0) {
      memo[key] = { ...answer, saved: Date.now() };
      writeMemo(memo);
    }
    return answer;
  } catch {
    // A stale memo still beats nothing when the call fails — same reasoning as
    // the server's stale-over-empty rule.
    return held ?? NOTHING;
  }
}

/**
 * Whether this device has already met the price feature.
 *
 * A device flag rather than a household field, because it records that a PERSON
 * has seen an explanation — a flatmate on their own phone deserves the same
 * introduction, and the household's city (which they will inherit) is a
 * different fact stored in a different place.
 */
/**
 * Whether the price introduction has been met — PER HOUSEHOLD.
 *
 * It was per device, and that was a bug with a specific shape: sign into a
 * second account on the same phone, open a shopping-list line, and nothing
 * happens. The doorway had been "seen", so it stayed shut; the new household
 * had no city, so no lookup was ever sent. Prices did not fail, they were
 * never asked for — and from the outside that is indistinguishable from the
 * feature not existing.
 *
 * The old device-wide key is still honoured so an existing install is not
 * re-introduced to a feature it already answered; the screen then falls back
 * to country-wide prices for a household with no city rather than to nothing.
 */
const INTRO_KEY_LEGACY = "larder.prices.intro.v1";
const introKey = (householdId: string) => `larder.prices.intro.v2.${householdId}`;

export function introSeen(householdId: string): boolean {
  try {
    return (
      localStorage.getItem(introKey(householdId)) === "1" ||
      localStorage.getItem(INTRO_KEY_LEGACY) === "1"
    );
  } catch {
    // Private mode. Being introduced twice is a far smaller cost than a crash.
    return false;
  }
}

export function markIntroSeen(householdId: string): void {
  try {
    localStorage.setItem(introKey(householdId), "1");
  } catch {
    /* Then it asks again next time, which is survivable. */
  }
}

/**
 * Asks the browser where we are, then asks the server what Google calls it.
 *
 * The permission prompt is the reason this is never automatic: it appears when
 * someone taps a button that says what it is for, which is the only time a
 * location prompt is answerable rather than alarming.
 */
export async function findWhere(): Promise<ShopLocation | null> {
  const fix = await currentPosition();
  if (!fix) return null;
  try {
    const res = await askWhere({ lat: fix.lat, lng: fix.lng });
    return res.data?.where ?? null;
  } catch {
    return null;
  }
}

/** The Geolocation API, as a promise, and never as a rejection. */
function currentPosition(): Promise<{ lat: number; lng: number } | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      // Denied, unavailable, or timed out. All three mean the same thing here.
      () => resolve(null),
      {
        // A city is the finest resolution any of this uses, so the cheap,
        // fast, battery-friendly fix is also the accurate-enough one.
        enableHighAccuracy: false,
        timeout: 12_000,
        // A fix from earlier today is a perfectly good answer to "which city".
        maximumAge: 6 * 60 * 60 * 1000,
      },
    );
  });
}
